import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setCsrfToken, clearCsrfToken, setJwt, clearJwt } from "./api";

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Refresh CSRF token in memory store (covers cross-origin deploys where
      // JS cannot read the cookie set by the backend domain).
      try {
        const r = await api.get("/auth/csrf");
        if (r?.data?.csrf_token) setCsrfToken(r.data.csrf_token);
      } catch (_e) { /* non-blocking */ }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /auth/me if returning from OAuth callback - AuthCallback will handle
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const loginJWT = async (email, password, remember_me = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me });
    // Cookie is set by backend; ALSO cache the CSRF token in JS memory for
    // cross-origin deployments where document.cookie is blind to it.
    if (data.csrf_token) setCsrfToken(data.csrf_token);
    // Iter42 — Safari/iPhone fix: persist Bearer JWT as fallback when cookies get wiped by ITP.
    if (data.token) setJwt(data.token, remember_me);
    setUser(data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.csrf_token) setCsrfToken(data.csrf_token);
    if (data.token) setJwt(data.token, true);  // new accounts default to "remember me"
    setUser(data.user);
    return data.user;
  };
  const loginWithGoogle = (role = "client") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    sessionStorage.setItem("oauth_role", role);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* logout from backend is best-effort */ }
    // Legacy cleanup for users who still have an old localStorage token from a previous build
    localStorage.removeItem("token");
    clearJwt();
    clearCsrfToken();
    setUser(null);
  };

  return (
    <Ctx.Provider value={{ user, loading, loginJWT, register, loginWithGoogle, logout, refresh: fetchMe }}>
      {children}
    </Ctx.Provider>
  );
}
